import i18next, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  DEFAULT_LANGUAGE,
  preferredLanguages,
  resolveLanguage,
  SUPPORTED_LANGUAGES,
  type Language,
} from './language';

import { storage, storageKey } from '@/lib/storage';

export {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  resolveLanguage,
  type Language,
} from './language';

export const LANGUAGE_STORAGE_KEY = storageKey('language');

/**
 * Namespaces loaded up front: those the first screen needs.
 *
 * `auth` is among them because signing in IS the first screen of an anonymous
 * session. Domain namespaces (library, sessions…) stay loaded on demand by
 * their own screen.
 */
export const INITIAL_NAMESPACES = ['common', 'nav', 'errors', 'auth'] as const;

/**
 * Lazy loading per namespace, with no extra dependency.
 *
 * Warning: `i18next-http-backend` is deliberately not used: it would make network
 * requests, and in an offline Capacitor WebView the translations would vanish.
 * `import.meta.glob` produces real Vite chunks, bundled natively and loaded on
 * demand.
 */
const loaders = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
);

const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const load = loaders[`./locales/${language}/${namespace}.json`];
    if (!load) {
      callback(
        new Error(`Traductions absentes : ${language}/${namespace}`),
        false,
      );
      return;
    }
    load()
      .then((module) => callback(null, module.default))
      .catch((error: unknown) => callback(error as Error, false));
  },
};

/**
 * The reader's explicit choice, then the browser or OS preferences, then the
 * default. All the selection logic lives in `./language`, tested apart.
 */
async function resolveInitialLanguage(): Promise<Language> {
  return resolveLanguage(
    await storage.get(LANGUAGE_STORAGE_KEY),
    preferredLanguages(),
  );
}

export async function initI18n() {
  await i18next
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      lng: await resolveInitialLanguage(),
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES,
      ns: [...INITIAL_NAMESPACES],
      defaultNS: 'common',
      // React already escapes everything it renders.
      interpolation: { escapeValue: false },
      returnNull: false,
      debug: import.meta.env.DEV,
    });

  // Warning: `<html lang>` must reflect the language actually rendered: it is what
  // screen readers use to pick their pronunciation and search engines to index.
  // `index.html` hard-codes `lang="fr"`, so without this line an English
  // reader gets a page announced as French (seen in verification).
  document.documentElement.lang = i18next.resolvedLanguage ?? DEFAULT_LANGUAGE;

  return i18next;
}

export async function changeLanguage(language: Language) {
  await i18next.changeLanguage(language);
  await storage.set(LANGUAGE_STORAGE_KEY, language);
  document.documentElement.lang = language;
}

export { i18next };
